import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';

export const Route = createFileRoute('/_authenticated/drivers/new')({
  component: DriverCreatePage,
});

// Mirrors the invite-driver Edge Function's hand-rolled validators.
// Kept loose on purpose — the function is the source of truth and we
// surface its error codes in friendlyCreateError below.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPLOYEE_ID_RE = /^[A-Za-z0-9-]+$/;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type PharmacyOption = {
  id: string;
  name: string;
  city: string | null;
};

type TenantOption = {
  id: string;
  name: string;
};

type FormState = {
  email: string;
  full_name: string;
  employee_id: string;
  phone: string;
  license_number: string;
  license_state: string;
  license_expires_on: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_color: string;
  vehicle_plate: string;
  vehicle_state: string;
  hire_date: string;
  pharmacy_ids: Set<string>;
  // Only used when caller is platform_admin.
  tenant_id: string;
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

type InviteDriverResponse = {
  user_id: string;
  email: string;
  invite_sent_at: string;
};

type InviteDriverError = {
  error?: string;
  message?: string;
};

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function emptyForm(): FormState {
  return {
    email: '',
    full_name: '',
    employee_id: '',
    phone: '',
    license_number: '',
    license_state: '',
    license_expires_on: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_color: '',
    vehicle_plate: '',
    vehicle_state: '',
    hire_date: todayIso(),
    pharmacy_ids: new Set(),
    tenant_id: '',
  };
}

// Translate the Edge Function's documented LYNRX_* and HTTP error
// surface (supabase/functions/invite-driver/index.ts) into user-facing
// copy. Falls back to a generic message for anything we haven't seen.
function friendlyCreateError(
  message: string,
  status: number,
): { field?: keyof FormState; text: string } {
  if (/LYNRX_FORBIDDEN/.test(message)) {
    return { text: "You don't have permission to create drivers." };
  }
  if (/LYNRX_EMAIL_REQUIRED/.test(message)) {
    return { field: 'email', text: 'Email is required.' };
  }
  if (/LYNRX_FULL_NAME_REQUIRED/.test(message)) {
    return { field: 'full_name', text: 'Full name is required.' };
  }
  if (/LYNRX_EMPLOYEE_ID_TAKEN/.test(message)) {
    return {
      field: 'employee_id',
      text: 'Employee ID is already in use in this tenant. Pick another.',
    };
  }
  if (/LYNRX_INVALID_PHARMACY/.test(message)) {
    return { text: 'One or more selected pharmacies are invalid.' };
  }
  if (
    status === 409 &&
    /email|exists|registered|taken|in use/i.test(message)
  ) {
    return {
      field: 'email',
      text: 'A user with that email already exists in this tenant.',
    };
  }
  if (status >= 500) {
    return { text: 'Something went wrong. Try again or contact support.' };
  }
  return { text: message || 'Could not create driver.' };
}

function validate(form: FormState, isPlatformAdmin: boolean): FieldErrors {
  const errors: FieldErrors = {};
  const email = form.email.trim();
  if (!email) {
    errors.email = 'Email is required.';
  } else if (!EMAIL_RE.test(email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!form.full_name.trim()) {
    errors.full_name = 'Full name is required.';
  }
  const empId = form.employee_id.trim();
  if (empId && !EMPLOYEE_ID_RE.test(empId)) {
    errors.employee_id =
      'Employee ID can only contain letters, numbers, and dashes.';
  }
  // License: if license_number is filled, state + expires_on become
  // required. This is a soft client-side rule — the backend does not
  // enforce it, the form does.
  const licenseFilled = form.license_number.trim().length > 0;
  if (licenseFilled && !form.license_state.trim()) {
    errors.license_state = 'License state is required when a license number is provided.';
  }
  if (licenseFilled && !form.license_expires_on) {
    errors.license_expires_on =
      'License expiry is required when a license number is provided.';
  }
  if (form.license_expires_on) {
    // Date input gives YYYY-MM-DD; compare as local calendar day to
    // avoid UTC offset edge cases.
    const parts = form.license_expires_on.split('-');
    if (parts.length === 3) {
      const y = Number(parts[0]);
      const m = Number(parts[1]);
      const d = Number(parts[2]);
      if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
        const exp = new Date(y, m - 1, d).getTime();
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        if (exp < today) {
          errors.license_expires_on = 'License expiry cannot be in the past.';
        }
      }
    }
  }
  if (isPlatformAdmin && !UUID_RE.test(form.tenant_id.trim())) {
    errors.tenant_id = 'Choose a tenant.';
  }
  return errors;
}

function nullIfBlank(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function buildRequestBody(
  form: FormState,
  isPlatformAdmin: boolean,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    email: form.email.trim(),
    full_name: form.full_name.trim(),
    employee_id: nullIfBlank(form.employee_id),
    phone: nullIfBlank(form.phone),
    license_number: nullIfBlank(form.license_number),
    license_state: nullIfBlank(form.license_state),
    license_expires_on: nullIfBlank(form.license_expires_on),
    vehicle_make: nullIfBlank(form.vehicle_make),
    vehicle_model: nullIfBlank(form.vehicle_model),
    vehicle_color: nullIfBlank(form.vehicle_color),
    vehicle_plate: nullIfBlank(form.vehicle_plate),
    vehicle_state: nullIfBlank(form.vehicle_state),
    hire_date: nullIfBlank(form.hire_date),
    pharmacy_ids: [...form.pharmacy_ids],
  };
  if (isPlatformAdmin) {
    body.tenant_id = form.tenant_id.trim();
  }
  return body;
}

function DriverCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const goBack = useCallback(() => {
    void navigate({ to: '/drivers', search: { status: 'active' } });
  }, [navigate]);
  return <DriverCreateView onCreated={goBack} />;
}

type ViewProps = {
  onCreated: () => void;
  // Test seam — defaults to the singleton supabase client.
  client?: LynrxSupabaseClient;
  // Test seam — defaults to global fetch.
  fetchImpl?: typeof fetch;
  // Optional override for the Supabase URL (env-driven by default).
  supabaseUrl?: string;
};

export function DriverCreateView({
  onCreated,
  client = defaultClient,
  fetchImpl,
  supabaseUrl,
}: ViewProps): JSX.Element {
  const { session, userType, isReady } = useAuth();
  const isPlatformAdmin = userType === 'platform_admin';

  useSetPageHeader({
    title: 'Add driver',
    subtitle: 'Onboard a new driver to your tenant.',
    parentLabel: 'Drivers',
    parentTo: '/drivers',
  });

  const [form, setForm] = useState<FormState>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [pharmacies, setPharmacies] = useState<PharmacyOption[] | null>(null);
  const [tenants, setTenants] = useState<TenantOption[] | null>(null);

  // Pharmacies dropdown: scoped by RLS to the caller's tenant.
  // platform_admin currently can't read public.pharmacies from the
  // client (the existing pharmacies_select_own_tenant policy filters
  // on JWT tenant_id, which is absent for platform_admin); they pass
  // pharmacy_ids by UUID in the request body if needed. For MVP this
  // is acceptable — pharmacy_admin is the primary caller.
  useEffect(() => {
    if (!isReady || !session) return;
    let cancelled = false;
    void (async () => {
      const { data, error } = await client
        .from('pharmacies')
        .select('id, name, city')
        .is('deleted_at', null)
        .order('name', { ascending: true });
      if (cancelled) return;
      if (error) {
        Sentry.captureException(error, {
          tags: { area: 'drivers.new.load_pharmacies' },
        });
        setPharmacies([]);
        return;
      }
      setPharmacies(
        (data ?? []).map((p) => ({
          id: p.id,
          name: p.name,
          city: p.city ?? null,
        })),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [client, isReady, session]);

  // Tenants dropdown (platform_admin only). The current
  // tenants_select_own_tenant RLS policy doesn't match for tokens
  // with no tenant_id claim — see the PR description for the gap.
  // If the query returns empty, the UI falls back to a UUID text
  // input so platform_admin can still proceed.
  useEffect(() => {
    if (!isReady || !session || !isPlatformAdmin) return;
    let cancelled = false;
    void (async () => {
      const { data, error } = await client
        .from('tenants')
        .select('id, name')
        .is('deleted_at', null)
        .order('name', { ascending: true });
      if (cancelled) return;
      if (error) {
        Sentry.captureException(error, {
          tags: { area: 'drivers.new.load_tenants' },
        });
        setTenants([]);
        return;
      }
      setTenants((data ?? []).map((t) => ({ id: t.id, name: t.name })));
    })();
    return () => {
      cancelled = true;
    };
  }, [client, isReady, session, isPlatformAdmin]);

  const update = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setFieldErrors((prev) => {
        if (!(key in prev)) return prev;
        const next: FieldErrors = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  const togglePharmacy = useCallback((id: string) => {
    setForm((prev) => {
      const next = new Set(prev.pharmacy_ids);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, pharmacy_ids: next };
    });
  }, []);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!session) return;
      setSubmitError(null);
      setSuccess(null);
      const errors = validate(form, isPlatformAdmin);
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;

      const resolvedUrl =
        supabaseUrl ?? (import.meta.env.VITE_SUPABASE_URL as string | undefined);
      if (!resolvedUrl) {
        setSubmitError('Supabase URL is not configured.');
        return;
      }

      setSubmitting(true);
      try {
        const endpoint = `${resolvedUrl.replace(/\/$/, '')}/functions/v1/invite-driver`;
        const doFetch = fetchImpl ?? fetch;
        const res = await doFetch(endpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(buildRequestBody(form, isPlatformAdmin)),
        });

        if (!res.ok) {
          let message = '';
          try {
            const body = (await res.json()) as InviteDriverError;
            message = body.message ?? body.error ?? '';
          } catch {
            // body not JSON; fall through to generic
          }
          Sentry.captureMessage('drivers.create.non_2xx', {
            level: 'error',
            tags: { status: String(res.status) },
          });
          const friendly = friendlyCreateError(message, res.status);
          if (friendly.field) {
            setFieldErrors((prev) => ({ ...prev, [friendly.field!]: friendly.text }));
          } else {
            setSubmitError(friendly.text);
          }
          return;
        }
        const data = (await res.json()) as InviteDriverResponse;
        setSuccess(
          `Driver created. Invite email sent to ${data.email}.`,
        );
        // Give the toast a moment to read, then redirect to the list.
        // No detail page exists yet — see PR brief.
        window.setTimeout(() => onCreated(), 1500);
      } catch (err) {
        Sentry.captureException(err, {
          tags: { area: 'drivers.create.network' },
        });
        setSubmitError('Could not reach the server. Try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [fetchImpl, form, isPlatformAdmin, onCreated, session, supabaseUrl],
  );

  const sectionTitleCls = 'text-sm font-semibold uppercase tracking-wide text-ink-2';
  const sectionWrapCls =
    'border-t border-border first:border-t-0 first:pt-0 pt-6 mt-6 first:mt-0';
  const gridCls = 'grid grid-cols-1 gap-4 md:grid-cols-2 mt-3';
  const disabled = submitting || Boolean(success);

  const pharmacyEmptyMessage = useMemo(() => {
    if (pharmacies === null) return 'Loading pharmacies…';
    if (pharmacies.length === 0) return 'No pharmacies available.';
    return null;
  }, [pharmacies]);

  return (
    <div className="max-w-3xl">
      <div className="mb-4">
        <Link to="/drivers" search={{ status: 'active' }} className="text-sm text-primary hover:underline">
          ← Back to drivers
        </Link>
      </div>

      {success ? (
        <div className="mb-4">
          <Alert tone="success">{success}</Alert>
        </div>
      ) : null}
      {submitError ? (
        <div className="mb-4">
          <Alert tone="error">{submitError}</Alert>
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="rounded-lg border border-border bg-bg p-6 shadow-sm" noValidate>
        {isPlatformAdmin ? (
          <section className={sectionWrapCls}>
            <h2 className={sectionTitleCls}>Tenant</h2>
            <p className="mt-1 text-xs text-ink-2">
              Choose which tenant this driver belongs to.
            </p>
            <div className={gridCls}>
              {tenants && tenants.length > 0 ? (
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium text-ink">Tenant</span>
                  <select
                    value={form.tenant_id}
                    onChange={(e) => update('tenant_id', e.target.value)}
                    disabled={disabled}
                    aria-invalid={Boolean(fieldErrors.tenant_id) || undefined}
                    className={`rounded-md border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary ${
                      fieldErrors.tenant_id ? 'border-red-500' : 'border-border'
                    }`}
                  >
                    <option value="">Select a tenant…</option>
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.tenant_id ? (
                    <p className="text-xs text-red-600">{fieldErrors.tenant_id}</p>
                  ) : null}
                </label>
              ) : (
                <Field
                  label="Tenant ID"
                  value={form.tenant_id}
                  onChange={(e) => update('tenant_id', e.target.value)}
                  disabled={disabled}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  hint="Paste the tenant's UUID. (RLS doesn't expose the tenant list to platform admins yet.)"
                  error={fieldErrors.tenant_id}
                />
              )}
            </div>
          </section>
        ) : null}

        <section className={sectionWrapCls}>
          <h2 className={sectionTitleCls}>Identity</h2>
          <div className={gridCls}>
            <Field
              label="Email"
              type="email"
              autoComplete="off"
              required
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              disabled={disabled}
              error={fieldErrors.email}
            />
            <Field
              label="Full name"
              required
              value={form.full_name}
              onChange={(e) => update('full_name', e.target.value)}
              disabled={disabled}
              error={fieldErrors.full_name}
            />
            <Field
              label="Employee ID"
              value={form.employee_id}
              onChange={(e) => update('employee_id', e.target.value)}
              disabled={disabled}
              placeholder="e.g. 4421"
              hint="Optional. Letters, numbers, and dashes only."
              error={fieldErrors.employee_id}
            />
            <Field
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              disabled={disabled}
              hint="Optional."
              error={fieldErrors.phone}
            />
          </div>
        </section>

        <section className={sectionWrapCls}>
          <h2 className={sectionTitleCls}>License</h2>
          <p className="mt-1 text-xs text-ink-2">
            All fields optional. State and expiry become required if a license number is provided.
          </p>
          <div className={gridCls}>
            <Field
              label="License number"
              value={form.license_number}
              onChange={(e) => update('license_number', e.target.value)}
              disabled={disabled}
              error={fieldErrors.license_number}
            />
            <Field
              label="License state"
              value={form.license_state}
              onChange={(e) => update('license_state', e.target.value.toUpperCase().slice(0, 2))}
              disabled={disabled}
              placeholder="MA"
              maxLength={2}
              error={fieldErrors.license_state}
            />
            <Field
              label="License expires on"
              type="date"
              value={form.license_expires_on}
              onChange={(e) => update('license_expires_on', e.target.value)}
              disabled={disabled}
              error={fieldErrors.license_expires_on}
            />
          </div>
        </section>

        <section className={sectionWrapCls}>
          <h2 className={sectionTitleCls}>Vehicle</h2>
          <p className="mt-1 text-xs text-ink-2">All fields optional.</p>
          <div className={gridCls}>
            <Field
              label="Make"
              value={form.vehicle_make}
              onChange={(e) => update('vehicle_make', e.target.value)}
              disabled={disabled}
            />
            <Field
              label="Model"
              value={form.vehicle_model}
              onChange={(e) => update('vehicle_model', e.target.value)}
              disabled={disabled}
            />
            <Field
              label="Color"
              value={form.vehicle_color}
              onChange={(e) => update('vehicle_color', e.target.value)}
              disabled={disabled}
            />
            <Field
              label="Plate"
              value={form.vehicle_plate}
              onChange={(e) => update('vehicle_plate', e.target.value)}
              disabled={disabled}
            />
            <Field
              label="Plate state"
              value={form.vehicle_state}
              onChange={(e) => update('vehicle_state', e.target.value.toUpperCase().slice(0, 2))}
              disabled={disabled}
              placeholder="MA"
              maxLength={2}
            />
          </div>
        </section>

        <section className={sectionWrapCls}>
          <h2 className={sectionTitleCls}>Operations</h2>
          <div className={gridCls}>
            <Field
              label="Hire date"
              type="date"
              value={form.hire_date}
              onChange={(e) => update('hire_date', e.target.value)}
              disabled={disabled}
              hint="Defaults to today."
              error={fieldErrors.hire_date}
            />
          </div>
          <div className="mt-4 flex flex-col gap-2 text-sm">
            <span className="font-medium text-ink">Pharmacy access</span>
            <p className="text-xs text-ink-2">
              Select the pharmacies this driver can deliver for. Leave empty to grant access later.
            </p>
            {pharmacyEmptyMessage ? (
              <p className="text-sm text-ink-2">{pharmacyEmptyMessage}</p>
            ) : (
              <div className="flex flex-col gap-1 rounded-md border border-border bg-bg-soft p-3">
                {(pharmacies ?? []).map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-2 text-sm text-ink"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={form.pharmacy_ids.has(p.id)}
                      onChange={() => togglePharmacy(p.id)}
                      disabled={disabled}
                    />
                    <span>
                      {p.name}
                      {p.city ? (
                        <span className="text-ink-2"> · {p.city}</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </section>

        <div className="mt-8 flex items-center justify-end gap-3">
          <Link
            to="/drivers"
            search={{ status: 'active' }}
            className="text-sm text-ink-2 hover:text-ink"
          >
            Cancel
          </Link>
          <Button type="submit" loading={submitting} disabled={disabled}>
            {submitting ? 'Creating…' : 'Create driver and send invite'}
          </Button>
        </div>
      </form>
    </div>
  );
}
